import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { uploadPublicAsset } from "@/lib/upload";
import { Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, IdentificationBadge, PaperPlaneRight, UploadSimple, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const DOCS = [
  { key: "photo", label: "Passport Photo", accept: "image/*" },
  { key: "id_proof", label: "ID / Passport", accept: "image/*,.pdf" },
  { key: "address_proof", label: "Address Proof", accept: "image/*,.pdf" },
  { key: "edu_proof", label: "Education Proof", accept: "image/*,.pdf" },
  { key: "bank_info", label: "Bank Info", accept: "image/*,.pdf" },
  { key: "cv", label: "CV / Resume", accept: "image/*,.pdf,.doc,.docx" },
];

export default function EmployeeOnboarding() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [docs, setDocs] = useState({});
  const [form, setForm] = useState({
    name: "", blood_group: "", phone: "", address: "", dob: "", health_issues: "",
    cloth_size: "", shoe_size: "", father_name: "", father_phone: "", mother_name: "",
    mother_phone: "", friend_name: "", friend_phone: "", emergency_contact: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/onboarding/public/${token}`);
        setForm((f) => ({ ...f, name: data.name || "" }));
        if (data.submitted) setDone(true);
      } catch (e) { setInvalid(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onDoc = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(key);
    try {
      const res = await uploadPublicAsset(file, token, "onboarding");
      setDocs((d) => ({ ...d, [key]: { url: res.url, name: file.name } }));
      toast.success("Uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(""); if (e.target) e.target.value = ""; }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const documents = Object.fromEntries(Object.entries(docs).map(([k, v]) => [k, v.url]));
      await api.post(`/onboarding/public/${token}/submit`, { ...form, documents });
      setDone(true); toast.success("Onboarding submitted");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inp = "bg-white border-slate-300";
  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="h-16 bg-blue-600 flex items-center px-6 gap-2.5 shrink-0">
        <img src="/freightx-logo.webp" alt="Asterism Services" className="w-8 h-8 object-contain" />
        <span className="text-white font-heading font-extrabold text-lg">Asterism Services</span>
      </header>
      <main className="flex-1 flex items-start justify-center p-4 sm:p-8"><div className="w-full max-w-3xl">{children}</div></main>
    </div>
  );

  if (loading) return <Shell><p className="text-slate-500 p-8 text-center">Loading…</p></Shell>;
  if (invalid) return <Shell><div className="bg-white border border-slate-200 rounded-xl p-10 text-center"><p className="font-heading font-bold text-slate-900 text-lg">Invalid or expired link</p><p className="text-sm text-slate-500 mt-1">Please contact HR for a new onboarding link.</p></div></Shell>;
  if (done) return (
    <Shell><div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="emp-onboarding-done">
      <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto mb-4" />
      <p className="font-heading font-extrabold text-slate-900 text-xl">Onboarding Complete!</p>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Thanks, {form.name}. Your details and documents have been submitted to the Asterism Services HR team.</p>
    </div></Shell>
  );

  return (
    <Shell>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center"><IdentificationBadge size={20} weight="duotone" className="text-blue-600" /></div>
          <div><h1 className="font-heading font-extrabold text-slate-900 text-xl leading-tight">Employee Onboarding</h1><p className="text-sm text-slate-500">Welcome to Asterism Services — tell us about yourself.</p></div>
        </div>

        <h3 className="font-heading font-bold text-slate-900 mb-3 text-sm uppercase tracking-wide">Personal</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name *"><Input data-testid="emp-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
          <Field label="Date of Birth"><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inp} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></Field>
          <Field label="Blood Group"><Input value={form.blood_group} onChange={(e) => set("blood_group", e.target.value)} className={inp} placeholder="e.g. O+" /></Field>
          <div className="sm:col-span-2"><Field label="Address"><Input value={form.address} onChange={(e) => set("address", e.target.value)} className={inp} /></Field></div>
          <Field label="Cloth Size"><Input value={form.cloth_size} onChange={(e) => set("cloth_size", e.target.value)} className={inp} placeholder="S / M / L / XL" /></Field>
          <Field label="Shoe Size"><Input value={form.shoe_size} onChange={(e) => set("shoe_size", e.target.value)} className={inp} /></Field>
          <div className="sm:col-span-2"><Field label="Health Issues (if any)"><Textarea value={form.health_issues} onChange={(e) => set("health_issues", e.target.value)} className={inp} rows={2} /></Field></div>
        </div>

        <h3 className="font-heading font-bold text-slate-900 mt-7 mb-3 text-sm uppercase tracking-wide">Family & Emergency</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Father's Name"><Input value={form.father_name} onChange={(e) => set("father_name", e.target.value)} className={inp} /></Field>
          <Field label="Father's Phone"><Input value={form.father_phone} onChange={(e) => set("father_phone", e.target.value)} className={inp} /></Field>
          <Field label="Mother's Name"><Input value={form.mother_name} onChange={(e) => set("mother_name", e.target.value)} className={inp} /></Field>
          <Field label="Mother's Phone"><Input value={form.mother_phone} onChange={(e) => set("mother_phone", e.target.value)} className={inp} /></Field>
          <Field label="Friend's Name"><Input value={form.friend_name} onChange={(e) => set("friend_name", e.target.value)} className={inp} /></Field>
          <Field label="Friend's Phone"><Input value={form.friend_phone} onChange={(e) => set("friend_phone", e.target.value)} className={inp} /></Field>
          <div className="sm:col-span-2"><Field label="Emergency Contact"><Input value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} className={inp} /></Field></div>
        </div>

        <h3 className="font-heading font-bold text-slate-900 mt-7 mb-3 text-sm uppercase tracking-wide">Documents</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DOCS.map((d) => (
            <label key={d.key} className={`flex items-center justify-between gap-3 rounded-lg border-2 border-dashed px-4 py-3 cursor-pointer transition-colors ${docs[d.key] ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-blue-300"}`} data-testid={`emp-doc-${d.key}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{d.label}</p>
                <p className="text-xs text-slate-400 truncate">{docs[d.key] ? docs[d.key].name : uploading === d.key ? "Uploading…" : "Tap to upload"}</p>
              </div>
              {docs[d.key] ? <Check size={20} className="text-emerald-500 shrink-0" weight="bold" /> : <UploadSimple size={20} className="text-slate-400 shrink-0" />}
              <input type="file" accept={d.accept} className="hidden" onChange={(e) => onDoc(e, d.key)} />
            </label>
          ))}
        </div>

        <div className="flex justify-end mt-7">
          <Button onClick={submit} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold px-6" data-testid="emp-submit">
            <PaperPlaneRight size={17} weight="fill" className="mr-1.5" />{saving ? "Submitting…" : "Submit Onboarding"}
          </Button>
        </div>
      </div>
    </Shell>
  );
}
